import { Link } from "wouter";
import { visibleCategories } from "@/lib/tools-data";
import { FEATURED_APP_IDS } from "@/lib/featured-apps-data";
import { getCategoryName } from "@/lib/i18n/category-persona-translations";
import { getToolName } from "@/lib/i18n/tool-name-translations";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useStructuredData } from "@/hooks/useStructuredData";
import { useLocale } from "@/hooks/useLocale";

export default function AllToolsPage() {
  const { locale, localizedHref } = useLocale();
  usePageMeta({
    title: "All Tools \u2014 Every Category, Every Tool",
    description:
      "Browse all 300+ FreeDailyPro tools by category \u2014 PDF, image, video, audio, text, calculators, and more, all in one page.",
    path: "/tools",
  });

  useStructuredData({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "All Tools",
    description: "Browse every online tool on FreeDailyPro.",
    url: "https://www.freedailypro.com/tools",
    isPartOf: {
      "@type": "WebSite",
      name: "FreeDailyPro",
      url: "https://www.freedailypro.com",
    },
  });

  return (
    <div className="page-enter">
      <div className="container py-10">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <Link href="/" className="hover:text-foreground transition-colors">
            Home
          </Link>
          <span>/</span>
          <span className="text-foreground font-medium">All Categories</span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight mb-3 gradient-text">
          All categories
        </h1>
        <p className="text-muted-foreground mb-10 max-w-2xl">
          300+ tools, organized by category — everything in one place,
          nothing truncated.
        </p>

        <div className="space-y-2">
          {visibleCategories.map(category => (
            <section
              key={category.id}
              className="py-8 border-b border-border/50 last:border-0"
            >
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${category.color}15` }}
                >
                  <category.icon
                    className="h-4.5 w-4.5"
                    style={{ color: category.color }}
                  />
                </div>
                <div>
                  <h2 className="text-lg font-semibold card-heading">
                    <Link
                      href={localizedHref(`/category/${category.id}`)}
                      className="hover:text-primary transition-colors"
                    >
                      {getCategoryName(locale, category.id, category.name)}
                    </Link>
                  </h2>
                  <span className="text-xs font-mono text-muted-foreground">
                    {category.tools.filter(t => !t.hidden && !FEATURED_APP_IDS.has(t.id)).length} TOOLS
                  </span>
                </div>
              </div>

              {/* Same .tool-chip pill treatment already used on CategoryGrid
                  and the Workflow Products page, not the plain unstyled text
                  links this page had before -- every tool stays listed (this
                  page's own promise is "nothing truncated"), only the visual
                  presentation changes to match. */}
              <div className="flex flex-wrap gap-2">
                {category.tools
                  .filter(tool => !tool.hidden && !FEATURED_APP_IDS.has(tool.id))
                  .map(tool => (
                    <Link key={tool.id} href={localizedHref(`/tool/${tool.id}`)} className="tool-chip">
                      {getToolName(locale, tool.id, tool.name)}
                    </Link>
                  ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
